// Admin → Centers & classrooms (SPEC §7.7): centers with inline edit, add, archive, delete if unused;
// classrooms grouped by center; link to travel times.

import type { ApiResult } from "../../api/client";
import {
  createCenter, deleteCenter, setCenterArchived, updateCenter, type CenterFields,
} from "../../api/centers";
import { createClassroom } from "../../api/classrooms";
import type { Center, Classroom } from "../../api/types";
import { t } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { isAdmin } from "../../session";
import { createConfirmButton } from "../../ui/confirmButton";
import { createButton, createElement, createLink } from "../../ui/dom";
import { createCheckbox, createInputField, createSelectField, type Field } from "../../ui/forms";
import { reportAndReload } from "../../ui/resultMessage";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { loadReferenceData, type ReferenceData } from "../referenceData";

export async function renderCentersPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("nav.centers"));
  page.append(createLoading());
  const reference: ApiResult<ReferenceData> = await loadReferenceData();
  if (reference.ok === false) {
    showPageProblem(page, reference.problem);
    return;
  }

  const includeArchived: boolean = route.query.get("archived") === "1";
  const draw = (): void => {
    drawPage(startPage(container, t("nav.centers")), reference.data, includeArchived);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(page: HTMLElement, reference: ReferenceData, includeArchived: boolean): void {
  const archived: Field<HTMLInputElement> = createCheckbox(t("app.showArchived"), includeArchived);
  archived.input.addEventListener("change", (): void => {
    navigate(archived.input.checked ? "/admin/centers?archived=1" : "/admin/centers");
  });
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(archived.wrapper, createLink("/admin/travel", t("admin.travelTimes")));
  const centers: Center[] = reference.centers.filter((center: Center): boolean => includeArchived ||
    center.archived === false);
  page.append(toolbar, createElement("h2", "", t("admin.centers")), buildCentersTable(centers));
  if (isAdmin()) {
    page.append(buildAddCenterForm());
  }

  page.append(createElement("h2", "", t("admin.classrooms")), ...centers.map((center: Center): HTMLElement =>
    buildClassroomList(center, reference.classrooms, includeArchived)));
  if (isAdmin()) {
    page.append(buildAddClassroomForm(centers.filter((center: Center): boolean => center.archived === false)));
  }
}

function buildCentersTable(centers: Center[]): HTMLElement {
  const wrapper: HTMLDivElement = createElement("div", "table-scroll");
  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.name"), t("admin.address"), t("app.note"), ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const center of centers) {
    body.append(isAdmin() ? buildEditableRow(center) : buildReadOnlyRow(center));
  }

  wrapper.append(table);
  return wrapper;
}

function buildReadOnlyRow(center: Center): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const name: HTMLTableCellElement = createElement("td");
  name.append(createLink(`/admin/centers/${center.id}`, center.name));
  row.append(name, createElement("td", "", center.address ?? ""), createElement("td", "", center.note ?? ""),
    createElement("td", "", center.archived ? t("app.archived") : ""));
  return row;
}

/** Inline edit: the row's inputs plus Save, Archive/Unarchive and Delete (SPEC §7.7). */
function buildEditableRow(center: Center): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const inputs: HTMLInputElement[] = [center.name, center.address ?? "", center.note ?? ""].map(
    (value: string): HTMLInputElement => {
      const input: HTMLInputElement = createElement("input");
      input.value = value;
      return input;
    });
  for (const input of inputs) {
    const cell: HTMLTableCellElement = createElement("td");
    cell.append(input);
    row.append(cell);
  }

  const actions: HTMLTableCellElement = createElement("td");
  actions.append(createLink(`/admin/centers/${center.id}`, t("admin.closures")), " ",
    createButton(t("app.save"), async (): Promise<void> => {
      const fields: CenterFields = { name: inputs[0]?.value ?? "", address: inputs[1]?.value ?? "",
        note: inputs[2]?.value ?? "" };
      await reportAndReload(await updateCenter(center.id, fields, center.version));
    }), buildArchiveToggle(center), createConfirmButton(t("app.delete"), async (): Promise<void> => {
      await reportAndReload(await deleteCenter(center.id));
    }));
  row.append(actions);
  return row;
}

function buildArchiveToggle(center: Center): HTMLButtonElement {
  return createButton(center.archived ? t("app.unarchive") : t("app.archive"), async (): Promise<void> => {
    await reportAndReload(await setCenterArchived(center.id, center.archived === false, center.version));
  });
}

function buildAddCenterForm(): HTMLElement {
  const name: Field<HTMLInputElement> = createInputField(t("app.name"), "text", "");
  const address: Field<HTMLInputElement> = createInputField(t("admin.address"), "text", "");
  const form: HTMLDivElement = createElement("div", "toolbar");
  form.append(name.wrapper, address.wrapper, createButton(t("admin.addCenter"), async (): Promise<void> => {
    await reportAndReload(await createCenter({ name: name.input.value, address: address.input.value, note: "" }));
  }, "button-primary"));
  return form;
}

function buildClassroomList(center: Center, classrooms: Classroom[], includeArchived: boolean): HTMLElement {
  const section: HTMLDivElement = createElement("div", "card");
  section.append(createElement("h3", "", center.name));
  const list: HTMLUListElement = createElement("ul");
  const own: Classroom[] = classrooms.filter((classroom: Classroom): boolean => classroom.centerId === center.id &&
    (includeArchived || classroom.archived === false));
  for (const classroom of own) {
    const item: HTMLLIElement = createElement("li");
    item.append(createLink(`/admin/classrooms/${classroom.id}`, classroom.name));
    if (classroom.archived) {
      item.append(" ", createElement("span", "badge badge-neutral", t("app.archived")));
    }

    list.append(item);
  }

  section.append(list);
  return section;
}

function buildAddClassroomForm(centers: Center[]): HTMLElement {
  const options: { value: string; label: string }[] = centers.map(
    (center: Center): { value: string; label: string } => ({ value: center.id, label: center.name }));
  const center: Field<HTMLSelectElement> = createSelectField(t("day.center"), options, centers[0]?.id ?? "");
  const name: Field<HTMLInputElement> = createInputField(t("app.name"), "text", "");
  const form: HTMLDivElement = createElement("div", "toolbar");
  form.append(center.wrapper, name.wrapper, createButton(t("admin.addClassroom"), async (): Promise<void> => {
    await reportAndReload(await createClassroom({
      centerId: center.input.value, name: name.input.value, capacity: null, note: "", weeklyUnavailable: [],
    }));
  }, "button-primary"));
  return form;
}
